/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: AI Video Generator 首屏视频播放器逻辑 Hook，支持无缝切换和 IndexedDB 缓存
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { videoCacheDB } from '@/components/HeroVideoPlayer/videoCacheDB';

import { CHARACTER_VIDEOS, CharacterVideo } from './characterData';

interface UseVideoGeneratorPlayerProps {
  characters?: CharacterVideo[];
  autoPlay?: boolean;
  loop?: boolean;
  defaultIndex?: number;
}

export const useVideoGeneratorPlayer = ({
  characters = CHARACTER_VIDEOS,
  autoPlay = true,
  loop = true,
  defaultIndex = 2,
}: UseVideoGeneratorPlayerProps = {}) => {
  const [currentIndex, setCurrentIndex] = useState(defaultIndex);
  const [isMuted, setIsMuted] = useState(true);
  const [isReady, setIsReady] = useState(false);

  // 视频元素引用
  const activeVideoRef = useRef<HTMLVideoElement>(null);
  const bufferVideoRef = useRef<HTMLVideoElement>(null);

  // 内存缓存 Blob URL
  const videoBlobCache = useRef<Map<string, string>>(new Map());

  // 当前角色
  const currentCharacter = characters[currentIndex];

  // 预加载视频为 Blob URL，优先从 IndexedDB 读取
  const preloadVideoAsBlob = useCallback(async (videoUrl: string) => {
    if (!videoUrl) return videoUrl;

    // 1. 检查内存缓存
    if (videoBlobCache.current.has(videoUrl)) {
      return videoBlobCache.current.get(videoUrl);
    }

    try {
      // 2. 尝试从 IndexedDB 读取
      const cachedBlob = await videoCacheDB.get(videoUrl);
      if (cachedBlob) {
        const blobUrl = URL.createObjectURL(cachedBlob);
        videoBlobCache.current.set(videoUrl, blobUrl);
        return blobUrl;
      }

      // 3. 从网络获取
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // 保存到内存缓存
      videoBlobCache.current.set(videoUrl, blobUrl);

      // 异步保存到 IndexedDB（不阻塞）
      videoCacheDB.set(videoUrl, blob).catch((error) => {
        console.warn('Failed to save video to IndexedDB:', error);
      });

      return blobUrl;
    } catch (error) {
      console.warn('预加载视频失败:', videoUrl, error);
      return videoUrl; // 失败则使用原始 URL
    }
  }, []);

  // 预加载所有视频（后台异步进行）
  useEffect(() => {
    characters.forEach((character) => {
      if (character.videoUrl) {
        preloadVideoAsBlob(character.videoUrl);
      }
    });

    // 清理函数：释放 Blob URL
    return () => {
      videoBlobCache.current.forEach((blobUrl) => {
        URL.revokeObjectURL(blobUrl);
      });
      videoBlobCache.current.clear();
    };
  }, [characters, preloadVideoAsBlob]);

  // 处理视频播放结束
  const handleVideoEnd = useCallback(() => {
    if (loop) {
      // 循环播放当前视频
      const video = activeVideoRef.current;
      if (video) {
        video.currentTime = 0;
        video.play().catch(console.warn);
      }
    }
  }, [loop]);

  // 切换角色
  const changeCharacter = useCallback(
    (index: number) => {
      if (index >= 0 && index < characters.length && index !== currentIndex) {
        setCurrentIndex(index);
        setIsReady(false);
      }
    },
    [characters.length, currentIndex],
  );

  // 上一个角色
  const previousCharacter = useCallback(() => {
    const newIndex = currentIndex === 0 ? characters.length - 1 : currentIndex - 1;
    changeCharacter(newIndex);
  }, [currentIndex, characters.length, changeCharacter]);

  // 下一个角色
  const nextCharacter = useCallback(() => {
    const newIndex = currentIndex === characters.length - 1 ? 0 : currentIndex + 1;
    changeCharacter(newIndex);
  }, [currentIndex, characters.length, changeCharacter]);

  // 切换静音状态
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  // 加载视频到指定的 video 元素
  const loadVideo = useCallback(
    async (videoElement: HTMLVideoElement, videoUrl: string, shouldMute?: boolean) => {
      if (!videoUrl) return;

      const targetMuted = shouldMute !== undefined ? shouldMute : isMuted;

      // 直接使用原始 URL，不使用 blob 缓存
      videoElement.src = videoUrl;
      videoElement.muted = targetMuted;
      videoElement.playsInline = true;

      return new Promise<void>((resolve, reject) => {
        const handleCanPlay = () => {
          videoElement.removeEventListener('canplay', handleCanPlay);
          videoElement.removeEventListener('error', handleError);
          resolve();
        };

        const handleError = (e: Event) => {
          videoElement.removeEventListener('canplay', handleCanPlay);
          videoElement.removeEventListener('error', handleError);
          reject(e);
        };

        videoElement.addEventListener('canplay', handleCanPlay);
        videoElement.addEventListener('error', handleError);
        videoElement.load();
      });
    },
    [isMuted, preloadVideoAsBlob],
  );

  // 处理视频源变化
  useEffect(() => {
    const activeVideo = activeVideoRef.current;
    const bufferVideo = bufferVideoRef.current;

    if (!activeVideo || !currentCharacter) return;

    // 如果没有视频 URL，只显示封面
    if (!currentCharacter.videoUrl) {
      setIsReady(true);
      return;
    }

    let isCancelled = false;

    const setupVideo = async () => {
      try {
        // 加载新视频
        await loadVideo(activeVideo, currentCharacter.videoUrl);

        if (isCancelled) return;

        // 自动播放
        if (autoPlay) {
          const playPromise = activeVideo.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
        }

        if (isCancelled) return;

        setIsReady(true);

        // 预加载下一个视频
        if (bufferVideo) {
          const nextIndex = (currentIndex + 1) % characters.length;
          const nextCharacter = characters[nextIndex];
          if (nextCharacter && nextCharacter.videoUrl !== currentCharacter.videoUrl) {
            loadVideo(bufferVideo, nextCharacter.videoUrl, isMuted).catch(() => {
              // 预加载失败不影响当前播放
            });
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.warn('视频播放失败:', error);
          setIsReady(true); // 即使失败也显示封面
        }
      }
    };

    // 添加视频结束事件监听
    activeVideo.addEventListener('ended', handleVideoEnd);

    setupVideo();

    return () => {
      isCancelled = true;
      activeVideo.removeEventListener('ended', handleVideoEnd);
    };
  }, [
    currentCharacter,
    currentIndex,
    characters,
    loadVideo,
    handleVideoEnd,
    autoPlay,
    isMuted,
  ]);

  // 更新静音状态
  useEffect(() => {
    const activeVideo = activeVideoRef.current;
    const bufferVideo = bufferVideoRef.current;

    if (activeVideo) {
      activeVideo.muted = isMuted;
    }
    if (bufferVideo) {
      bufferVideo.muted = isMuted;
    }
  }, [isMuted]);

  return {
    activeVideoRef,
    bufferVideoRef,
    currentCharacter,
    currentIndex,
    characters,
    isMuted,
    isReady,
    toggleMute,
    changeCharacter,
    previousCharacter,
    nextCharacter,
  };
};
